import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";

import Home from "@/pages/Home";
import Login from "@/pages/Login";
import Welcome from "@/pages/Welcome";
import Facials from "@/pages/Facials";
import PortalLayout from "@/pages/portal/PortalLayout";
import Dashboard from "@/pages/portal/Dashboard";
import Gallery from "@/pages/portal/Gallery";
import BookAppointment from "@/pages/portal/BookAppointment";
import Messages from "@/pages/portal/Messages";
import Subscription from "@/pages/portal/Subscription";
import Referrals from "@/pages/portal/Referrals";
import AdminServices from "@/pages/portal/AdminServices";
import AdminLeads from "@/pages/portal/AdminLeads";
import AdminInstagram from "@/pages/portal/AdminInstagram";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import CheckoutCancel from "@/pages/CheckoutCancel";
import SkinQuiz from "@/pages/SkinQuiz";
import Jacksonville from "@/pages/cities/Jacksonville";
import StAugustine from "@/pages/cities/StAugustine";
import OrangePark from "@/pages/cities/OrangePark";
import Weddings from "@/pages/Weddings";
import AuthCallback from "@/pages/AuthCallback";

const TOAST_OPTIONS = {
    style: { background: "#160F1A", border: "1px solid #362640", color: "#fff" },
};

function ProtectedRoute({ children }) {
    const { user, loading } = useAuth();
    if (loading) {
        return (
            <div className="min-h-screen bg-ink flex items-center justify-center">
                <div className="w-10 h-10 border-2 border-rose/30 border-t-rose rounded-full animate-spin" />
            </div>
        );
    }
    if (!user) return <Navigate to="/login" replace />;
    return children;
}

function AppRouter() {
    const location = useLocation(); // read hash from here, not window.location.hash (not reactive)
    // Google OAuth callback: session_id arrives in the URL fragment — process it before any route runs
    if (location.hash?.includes("session_id=")) return <AuthCallback />;
    return (
        <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/facials" element={<Facials />} />
            <Route path="/skin-quiz" element={<SkinQuiz />} />
            <Route path="/jacksonville" element={<Jacksonville />} />
            <Route path="/staugustine" element={<StAugustine />} />
            <Route path="/orangepark" element={<OrangePark />} />
            <Route path="/weddings" element={<Weddings />} />
            <Route path="/login" element={<Login />} />
            <Route path="/welcome" element={<ProtectedRoute><Welcome /></ProtectedRoute>} />
            <Route path="/checkout/success" element={<ProtectedRoute><CheckoutSuccess /></ProtectedRoute>} />
            <Route path="/checkout/cancel" element={<ProtectedRoute><CheckoutCancel /></ProtectedRoute>} />
            <Route path="/portal" element={<ProtectedRoute><PortalLayout /></ProtectedRoute>}>
                <Route index element={<Dashboard />} />
                <Route path="gallery" element={<Gallery />} />
                <Route path="book" element={<BookAppointment />} />
                <Route path="messages" element={<Messages />} />
                <Route path="subscription" element={<Subscription />} />
                <Route path="referrals" element={<Referrals />} />
                <Route path="admin/services" element={<AdminServices />} />
                <Route path="admin/leads" element={<AdminLeads />} />
                <Route path="admin/instagram" element={<AdminInstagram />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

function ScrollToTop() {
    const { pathname } = useLocation();
    useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, [pathname]);
    return null;
}

function App() {
    return (
        <div className="App bg-ink text-white">
            <BrowserRouter>
                <AuthProvider>
                    <ScrollToTop />
                    <AppRouter />
                    <Toaster
                        theme="dark"
                        toastOptions={TOAST_OPTIONS}
                    />
                </AuthProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
